/**
 * Development-only logging utility
 * Provides consistent logging patterns with automatic NODE_ENV checks
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LoggerConfig {
  /** Namespace/prefix for log messages (e.g., 'CMS-Transformer', 'UI-Component') */
  namespace?: string;
  /** Whether to include timestamps in development logs */
  includeTimestamp?: boolean;
  /** Minimum log level in development (default: 'debug') */
  minLevel?: LogLevel;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

/**
 * Creates a namespaced logger for development-only logging
 */
export function createDevLogger(config: LoggerConfig = {}) {
  const { namespace, includeTimestamp = false, minLevel = 'debug' } = config;

  const isDevelopment = process.env.NODE_ENV === 'development';
  const minLevelValue = LOG_LEVELS[minLevel];

  const formatMessage = (level: LogLevel, message: string): string => {
    const parts: string[] = [];

    if (includeTimestamp) {
      parts.push(`[${new Date().toISOString()}]`);
    }

    if (namespace) {
      parts.push(`[${namespace}]`);
    }

    parts.push(message);
    return parts.join(' ');
  };

  const shouldLog = (level: LogLevel): boolean => {
    return isDevelopment && LOG_LEVELS[level] >= minLevelValue;
  };

  return {
    /**
     * Debug-level logging (development only)
     * Use for detailed debugging information
     */
    debug: (message: string, ...args: any[]) => {
      if (shouldLog('debug')) {
        console.log(formatMessage('debug', message), ...args);
      }
    },

    /**
     * Info-level logging (development only)
     * Use for general information
     */
    info: (message: string, ...args: any[]) => {
      if (shouldLog('info')) {
        console.info(formatMessage('info', message), ...args);
      }
    },

    /**
     * Warning-level logging (development only)
     * Use for non-critical issues
     */
    warn: (message: string, ...args: any[]) => {
      if (shouldLog('warn')) {
        console.warn(formatMessage('warn', message), ...args);
      }
    },

    /**
     * Error-level logging (always enabled)
     * Use for critical issues that should be logged in all environments
     */
    error: (message: string, ...args: any[]) => {
      console.error(formatMessage('error', message), ...args);
    },

    /**
     * Group logging for related messages (development only)
     */
    group: (label: string, callback: () => void) => {
      if (isDevelopment) {
        console.group(formatMessage('info', label));
        callback();
        console.groupEnd();
      }
    },

    /**
     * Performance timing (development only)
     */
    time: (label: string) => {
      if (isDevelopment) {
        console.time(formatMessage('debug', `Timer: ${label}`));
      }
    },

    /**
     * End performance timing (development only)
     */
    timeEnd: (label: string) => {
      if (isDevelopment) {
        console.timeEnd(formatMessage('debug', `Timer: ${label}`));
      }
    },

    /**
     * Table logging for structured data (development only)
     */
    table: (data: any, columns?: string[]) => {
      if (isDevelopment) {
        console.table(data, columns);
      }
    },

    /**
     * Check if development logging is enabled
     */
    get isDevelopment() {
      return isDevelopment;
    },

    /**
     * Get current configuration
     */
    get config() {
      return { namespace, includeTimestamp, minLevel };
    },
  };
}

/**
 * Default logger instance for general use
 */
export const devLogger = createDevLogger({
  namespace: 'App',
  includeTimestamp: false,
});

/**
 * Quick development-only logging functions
 */
export const devLog = {
  /**
   * Quick debug log (development only)
   */
  debug: (message: string, ...args: any[]) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[DEBUG] ${message}`, ...args);
    }
  },

  /**
   * Quick info log (development only)
   */
  info: (message: string, ...args: any[]) => {
    if (process.env.NODE_ENV === 'development') {
      console.info(`[INFO] ${message}`, ...args);
    }
  },

  /**
   * Quick warning log (development only)
   */
  warn: (message: string, ...args: any[]) => {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[WARN] ${message}`, ...args);
    }
  },

  /**
   * Quick error log (always enabled)
   */
  error: (message: string, ...args: any[]) => {
    console.error(`[ERROR] ${message}`, ...args);
  },
};

/**
 * Performance logging utilities
 */
export const devPerf = {
  /**
   * Measure execution time of a function (development only)
   */
  measure: <T>(label: string, fn: () => T): T => {
    if (process.env.NODE_ENV === 'development') {
      console.time(`[PERF] ${label}`);
      const result = fn();
      console.timeEnd(`[PERF] ${label}`);
      return result;
    }
    return fn();
  },

  /**
   * Measure async execution time (development only)
   */
  measureAsync: async <T>(label: string, fn: () => Promise<T>): Promise<T> => {
    if (process.env.NODE_ENV === 'development') {
      console.time(`[PERF] ${label}`);
      const result = await fn();
      console.timeEnd(`[PERF] ${label}`);
      return result;
    }
    return fn();
  },

  /**
   * Log memory usage (development only)
   */
  memory: (label?: string) => {
    if (
      process.env.NODE_ENV === 'development' &&
      typeof window !== 'undefined' &&
      'performance' in window
    ) {
      const memory = (performance as any).memory;
      if (memory) {
        const prefix = label ? `[MEMORY] ${label}:` : '[MEMORY]';
        console.log(`${prefix}`, {
          used: `${(memory.usedJSHeapSize / 1024 / 1024).toFixed(2)} MB`,
          total: `${(memory.totalJSHeapSize / 1024 / 1024).toFixed(2)} MB`,
          limit: `${(memory.jsHeapSizeLimit / 1024 / 1024).toFixed(2)} MB`,
        });
      }
    }
  },
};

/**
 * Component-specific logger for React components
 */
export const createComponentLogger = (componentName: string) => {
  return createDevLogger({
    namespace: `Component:${componentName}`,
    includeTimestamp: false,
  });
};

/**
 * CMS-specific logger for transformations
 */
export const createCMSLogger = (adapterName: string) => {
  return createDevLogger({
    namespace: `CMS:${adapterName}`,
    includeTimestamp: false,
  });
};
